import { z } from 'zod';
export const relationshipsContactIdSchema = z.string().uuid();
export const relationshipsOrganizationIdSchema = z.string().uuid();
export const relationshipsContactTypeSchema = z.enum([
    'customer',
    'adjuster',
    'agent',
    'property_manager',
    'vendor',
    'internal',
    'other',
]);
export const relationshipsContactStatusSchema = z.enum([
    'active',
    'difficult',
    'vip',
    'inactive',
]);
export const relationshipsContactEmailSchema = z.strictObject({
    email: z.string().email().max(320),
    isPrimary: z.boolean(),
    label: z.string().trim().max(50).nullable(),
});
export const relationshipsContactSchema = z.strictObject({
    id: relationshipsContactIdSchema,
    tenantId: z.string().uuid(),
    organizationId: relationshipsOrganizationIdSchema.nullable(),
    organizationName: z.string().max(240).nullable(),
    firstName: z.string().min(1).max(120),
    lastName: z.string().max(120).nullable(),
    email: z.string().email().max(320).nullable(),
    phone: z.string().max(60).nullable(),
    alternatePhone: z.string().max(60).nullable(),
    title: z.string().max(160).nullable(),
    type: relationshipsContactTypeSchema,
    status: relationshipsContactStatusSchema,
    notes: z.string().max(10_000).nullable(),
    notifySms: z.boolean(),
    notifyEmail: z.boolean(),
    // Deliberate audited consent records, distinct from the notify_* delivery
    // preferences; false is the do-not-contact opt-out for that channel.
    outboundSmsAuthorized: z.boolean(),
    outboundSmsAuthorizedAt: z.string().datetime({ offset: true }).nullable(),
    outboundEmailAuthorized: z.boolean(),
    outboundEmailAuthorizedAt: z.string().datetime({ offset: true }).nullable(),
    emails: z.array(relationshipsContactEmailSchema).max(10),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const relationshipsContactListQuerySchema = z.strictObject({
    search: z.string().trim().min(1).max(160).optional(),
    type: relationshipsContactTypeSchema.optional(),
    status: relationshipsContactStatusSchema.optional(),
    organizationId: relationshipsOrganizationIdSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).default(0),
});
const relationshipsCreateEmailSchema = z.strictObject({
    email: z.string().trim().email().max(320),
    isPrimary: z.boolean().optional(),
    label: z.string().trim().max(50).nullable().optional(),
});
export const relationshipsCreateContactSchema = z.strictObject({
    organizationId: relationshipsOrganizationIdSchema.nullable().default(null),
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().max(120).nullable().default(null),
    email: z.string().trim().email().max(320).nullable().default(null),
    phone: z.string().trim().max(60).nullable().default(null),
    alternatePhone: z.string().trim().max(60).nullable().default(null),
    title: z.string().trim().max(160).nullable().default(null),
    type: relationshipsContactTypeSchema.default('customer'),
    status: relationshipsContactStatusSchema.default('active'),
    notes: z.string().trim().max(10_000).nullable().default(null),
    notifySms: z.boolean().default(true),
    notifyEmail: z.boolean().default(true),
    emails: z.array(relationshipsCreateEmailSchema).max(10).default([]),
});
// Partial edit with the same allowlist as creation. `emails` left undefined
// leaves the stored email addresses untouched; provided, it fully replaces
// them. Consent flags change only through the dedicated outbound-authorization
// command, never through ordinary edits.
export const relationshipsUpdateContactSchema = z.strictObject({
    organizationId: relationshipsOrganizationIdSchema.nullable().optional(),
    firstName: z.string().trim().min(1).max(120).optional(),
    lastName: z.string().trim().max(120).nullable().optional(),
    email: z.string().trim().email().max(320).nullable().optional(),
    phone: z.string().trim().max(60).nullable().optional(),
    alternatePhone: z.string().trim().max(60).nullable().optional(),
    title: z.string().trim().max(160).nullable().optional(),
    type: relationshipsContactTypeSchema.optional(),
    status: relationshipsContactStatusSchema.optional(),
    notes: z.string().trim().max(10_000).nullable().optional(),
    notifySms: z.boolean().optional(),
    notifyEmail: z.boolean().optional(),
    emails: z.array(relationshipsCreateEmailSchema).max(10).optional(),
});
export const relationshipsContactOutboundAuthorizationSchema = z.strictObject({
    channel: z.enum(['sms', 'email']),
    authorized: z.boolean(),
    note: z.string().trim().min(1).max(500).nullable().default(null),
});
export const relationshipsContactsResponseSchema = z.strictObject({
    data: z.array(relationshipsContactSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const relationshipsContactResponseSchema = z.strictObject({
    data: relationshipsContactSchema,
});
//# sourceMappingURL=relationships-contact.contract.js.map